"use client"

import type { Omit } from "@wagmi/core/internal"
import { type Config, useChainId, useConfig } from "wagmi"
import type { GetVaultInterestRateModelInfoErrorType } from "../actions/getVaultInterestRateModelInfo"
import {
  type GetVaultInterestRateModelInfoData,
  type GetVaultInterestRateModelInfoOptions,
  type GetVaultInterestRateModelInfoQueryFnData,
  type GetVaultInterestRateModelInfoQueryKey,
  getVaultInterestRateModelInfoQueryOptions,
} from "../query"

import { Evaluate } from "viem"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UseVaultInterestRateModelInfoParameters<
  config extends Config = Config,
  selectData = GetVaultInterestRateModelInfoData,
> = Evaluate<
  GetVaultInterestRateModelInfoOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetVaultInterestRateModelInfoQueryFnData,
                GetVaultInterestRateModelInfoErrorType,
                selectData,
                GetVaultInterestRateModelInfoQueryKey
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
      select?: (data: GetVaultInterestRateModelInfoQueryFnData) => selectData
    }
>

export type UseVaultInterestRateModelInfoReturnType<
  selectData = GetVaultInterestRateModelInfoData,
> = UseQueryReturnType<selectData, GetVaultInterestRateModelInfoErrorType>

export function useVaultInterestRateModelInfo<
  config extends ExtendedConfig,
  selectData = GetVaultInterestRateModelInfoData,
>(
  parameters: UseVaultInterestRateModelInfoParameters<config, selectData> = {},
): UseVaultInterestRateModelInfoReturnType<selectData> {
  const {
    query = {},
    address,
    evcAddress,
    vaultLensAddress,
    scopeKey,
    cash,
    borrows,
  } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getVaultInterestRateModelInfoQueryOptions(
    config as ExtendedConfig,
    {
      address,
      cash,
      borrows,
      chainId: parameters.chainId ?? chainId,
      evcAddress,
      vaultLensAddress,
      scopeKey,
    },
  )
  const enabled = Boolean(address && (query.enabled ?? true))

  return useQuery({
    ...query,
    ...options,
    queryKey,
    enabled,
    retry: false,
    refetchOnMount: false,
  })
}
